import type { PracticeSchedule } from "../types";

/* Builds a downloadable iCalendar (RFC 5545) reminder from a practice cadence.
   One VEVENT with a weekly RRULE + an optional VALARM, so the learner's own
   calendar does the reminding — we never push notifications ourselves.

   Times are written as "floating" local date-times (no TZID, no trailing Z): the
   calendar app interprets "18:00" in whatever timezone the user is in, which is
   exactly what a personal practice reminder wants. No VTIMEZONE block needed. */

// ICS BYDAY codes, Monday-first to match our 0=Mon … 6=Sun day indices.
const BYDAY = ["MO", "TU", "WE", "TH", "FR", "SA", "SU"];

const pad = (n: number) => String(n).padStart(2, "0");

/** Weekday index (0=Mon … 6=Sun) for a Date, from JS getDay() (0=Sun). */
const mondayIndex = (d: Date) => (d.getDay() + 6) % 7;

/** First date on/after `from` whose weekday is in `days` (0=Mon). */
function firstOccurrence(days: number[], from: Date): Date {
  for (let i = 0; i < 7; i++) {
    const d = new Date(from.getFullYear(), from.getMonth(), from.getDate() + i);
    if (days.includes(mondayIndex(d))) return d;
  }
  return from;
}

/** Floating local date-time stamp, e.g. 20260627T180000 (no Z, no TZID). */
function floatingStamp(d: Date, time: string): string {
  const [h, m] = time.split(":").map(Number);
  return `${d.getFullYear()}${pad(d.getMonth() + 1)}${pad(d.getDate())}T${pad(h)}${pad(m)}00`;
}

/** UTC stamp for DTSTAMP (creation time), e.g. 20260627T120000Z. */
function utcStamp(d: Date): string {
  return (
    `${d.getUTCFullYear()}${pad(d.getUTCMonth() + 1)}${pad(d.getUTCDate())}` +
    `T${pad(d.getUTCHours())}${pad(d.getUTCMinutes())}${pad(d.getUTCSeconds())}Z`
  );
}

/** Returns the .ics text for a schedule, or null if there's nothing to export. */
export function buildPracticeICS(schedule: PracticeSchedule, now: Date = new Date()): string | null {
  const days = [...schedule.days].filter((d) => d >= 0 && d <= 6).sort((a, b) => a - b);
  if (days.length === 0) return null;

  const start = firstOccurrence(days, now);
  const uid = `practice-${start.getFullYear()}${pad(start.getMonth() + 1)}${pad(start.getDate())}-${Math.random()
    .toString(36)
    .slice(2, 8)}@ciaotutor`;

  const lines = [
    "BEGIN:VCALENDAR",
    "VERSION:2.0",
    "PRODID:-//CiaoTutor//Practice Plan//EN",
    "CALSCALE:GREGORIAN",
    "METHOD:PUBLISH",
    "BEGIN:VEVENT",
    `UID:${uid}`,
    `DTSTAMP:${utcStamp(now)}`,
    `DTSTART:${floatingStamp(start, schedule.time)}`,
    "DURATION:PT10M",
    `RRULE:FREQ=WEEKLY;BYDAY=${days.map((d) => BYDAY[d]).join(",")}`,
    "SUMMARY:Italiano con Elena",
    "DESCRIPTION:Dieci minuti di pratica con Elena. A little\\, often.",
  ];

  if (schedule.reminders) {
    const n = Math.max(0, schedule.reminder_minutes_before | 0);
    lines.push(
      "BEGIN:VALARM",
      "ACTION:DISPLAY",
      "DESCRIPTION:Italiano con Elena",
      `TRIGGER:-PT${n}M`,
      "END:VALARM",
    );
  }

  lines.push("END:VEVENT", "END:VCALENDAR");
  return lines.join("\r\n");
}

/** Triggers a browser download of the given .ics text. */
export function downloadICS(filename: string, content: string): void {
  const blob = new Blob([content], { type: "text/calendar;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename.endsWith(".ics") ? filename : `${filename}.ics`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}
